import { TourFeature } from './tour-feature.enum';

/**
 * Erklärvideos zu einzelnen Schritten der Einrichtungs-Checkliste.
 *
 * Manche Schritte lassen sich schneller zeigen als führen. Der Mentor bringt
 * den Nutzer durch **eine** erste Planung; das Video daneben zeigt den
 * kompletten Weg vom Wochenmuster bis zum freigegebenen Plan an einem
 * durchgehenden Beispiel — vier Minuten, ohne dass jemand im eigenen Mandanten
 * etwas anfassen muss.
 *
 * **Ergänzung, kein Ersatz.** Der Erledigt-Status eines Checklisten-Eintrags
 * hängt weiterhin allein an der Tour. Ein Video zählt bewusst nicht als
 * erledigt: Gesehen zu haben ist nicht dasselbe wie getan zu haben, und der
 * Funnel misst Ergebnisse (erste Abteilung, erster Plan), keine Wiedergaben.
 * Wer das ändern will, braucht einen eigenen Fortschritts-Status im Backend —
 * das Muster dafür steht in
 * `component/registered-stepper/intro-video.config.ts` (PageStatus +
 * ChecklistStep + Heartbeat-Telemetrie).
 */
export interface HelpVideo {
  /**
   * YouTube-Video-ID (der Teil hinter `youtu.be/`).
   *
   * Auf {@link HELP_VIDEO_PLACEHOLDER} stehen zu lassen blendet den
   * Video-Knopf aus — so kann der Eintrag hier stehen, bevor das Video
   * hochgeladen ist, ohne einen toten Knopf auszuliefern.
   */
  videoId: string;
  /** Überschrift im Dialog. */
  titleKey: string;
  /** Ein Satz unter dem Titel: was das Video zeigt. */
  descriptionKey: string;
  /** Spieldauer, für den Hinweis am Knopf („4 Min."). */
  durationMinutes: number;
}

/** Solange dieser Wert steht, ist der Eintrag stumm geschaltet. */
export const HELP_VIDEO_PLACEHOLDER = 'PLACEHOLDER_VIDEO_ID';

const HELP_VIDEOS: Partial<Record<TourFeature, HelpVideo>> = {
  [TourFeature.MentorEntry]: {
    // ⚠️ Nach dem Upload die echte ID eintragen — bis dahin bleibt der Knopf
    // unsichtbar. Datei: `landingpage/VideoEditing/out/schichtplanung-
    // einweisung/einweisung-youtube.mp4`, Beschreibungstext daneben in
    // `einweisung-youtube.md`.
    videoId: HELP_VIDEO_PLACEHOLDER,
    titleKey: 'HELP_VIDEO.MENTOR_ENTRY.TITLE',
    descriptionKey: 'HELP_VIDEO.MENTOR_ENTRY.DESC',
    durationMinutes: 5,
  },
};

/**
 * Liefert das Video zu einem Checklisten-Schritt — oder `undefined`, wenn es
 * keins gibt oder die ID noch nicht eingetragen ist.
 */
export function helpVideoFor(id: TourFeature): HelpVideo | undefined {
  const video = HELP_VIDEOS[id];
  return video && video.videoId !== HELP_VIDEO_PLACEHOLDER ? video : undefined;
}
